import React, { useState, useEffect, useRef } from 'react';
import CloseModalIcon from '../../icons/close_modal.svg';
import ToyotaButton from '../ToyotaButton';
import Typography from '../Typography';
import { useMobileBreakpoint } from '../breakpoints';
import { useTranslation } from 'react-i18next';
import { FaqCollapsed } from './components/FaqCollapsed';
import { PatentsCollapsed } from './components/PatentsCollapsed';
import { PatentDescription } from './components/PatentDescription';
import useSplitArray from './hooks/useSplitArray';
import HtmlText from '../HtmlText';

import {
  StyledModal,
  ModalBody,
  Heading,
  PatentsContainer,
  PatentsSection,
  Faqs,
  Footer,
  LicensingCategories,
  PatentHeading,
  USPatents,
  USPatentApplications,
  FooterHeading,
  PreviousPortfolio,
  NextPortfolio,
  LicensingList,
  SelectorSection,
  LongDescription,
  PatentsReference,
  PatentsDescription,
  Question,
  Answer,
  NextPortolioButton,
  CloseButton,
  License,
  Arrow,
  DescriptionSummary,
  LicensingContent,
  ListSection,
  PatentsList, Patent, PatentNumbers, HeadingImage, ImageContainer, FaqsList, Faq, PortfolioDescription, HeadingDescription, Subtitle, CTAWrapper, PatentsTitle, PortfolioTitle
} from './index.styled';


const circularIncrement = (length, index) => {
  if (index < length - 1) return index + 1
  else return 0
}

const circularDecrement = (length, index) => {
  if (index > 0) return index - 1
  else return length - 1
}

const reduceDescription = string => {
  const reduced = string.replace(/<br\/><br\/>/g, ' ').slice(0, 140);
  return reduced + '...';
}

export const PortfolioDetailModal = ({ updateModal, openModal, data }) => {
  const { t } = useTranslation();
  const portfolioCategories = t(`PortfolioCategories`, { returnObjects: true });
  const isMobile = useMobileBreakpoint();
  const currentSection = portfolioCategories.find(section => section.name === data.sectionName);
  const portfolios = currentSection.portfolios;
  const [index, setIndex] = useState(portfolios.findIndex(portfolio => portfolio.title === data.title));
  const [nextIndex, setNextIndex] = useState(index < portfolios.length - 1 ? index + 1 : 0);
  const [prevIndex, setPrevIndex] = useState(index === 0 ? portfolios.length - 1 : index - 1);
  const [current, setCurrent] = useState(() => portfolios[index]);
  const [next, setNext] = useState(() => portfolios[nextIndex]);
  const [prev, setPrev] = useState(() => portfolios[prevIndex]);
  const [open, setOpen] = useState(openModal);
  const currentFaqs = useSplitArray(current.faqs)
  const modalRef = useRef(null)

  const toggleModal = () => updateModal(!open);
  
  const kebabCaseTitle = current.title.replace(/([a-z])([A-Z])/g, '$1-$2').replace(/\s+/g, '-').toLowerCase();
  const gaGroup = (window.localStorage.i18nextLng === 'ja') ? 'ja' : 'en';

  useEffect(() => {
    setCurrent(() => portfolios[index]);
    setPrev(() => portfolios[prevIndex]);
    setNext(() => portfolios[nextIndex]);
    
    if (window.gtag) {  
      window.gtag('event', 'view', { 'event_category': `${currentSection.name}`, 'event_label': `${gaGroup}:${kebabCaseTitle}:view`, 'send_to': gaGroup });
    }
    //eslint-disable-next-line 
  }, [prevIndex, index, nextIndex]);

  const showNext = e => {
    e.preventDefault();
    setIndex(countIndex => circularIncrement(portfolios.length, countIndex));
    setNextIndex(countNext => circularIncrement(portfolios.length, countNext));
    setPrevIndex(countPrev => circularIncrement(portfolios.length, countPrev));
    modalRef.current.parentElement.scrollTo({ top: 0, scrollBehavior: 'smooth' });

    if (window.gtag) {
      window.gtag('event', 'nextprev-click', { 'event_category': `${currentSection.name}`, 'event_label': `${gaGroup}:${kebabCaseTitle}:next`, 'send_to': gaGroup });
    }
  }

  const showPrev = e => {
    e.preventDefault();
    setIndex(countIndex => circularDecrement(portfolios.length, countIndex));
    setNextIndex(countNext => circularDecrement(portfolios.length, countNext));
    setPrevIndex(countPrev => circularDecrement(portfolios.length, countPrev));
    modalRef.current.parentElement.scrollTo({ top: 0, scrollBehavior: 'smooth' });
  
    // Track contact us click
    if (window.gtag) {
      window.gtag('event', 'nextprev-click', { 'event_category': `${currentSection.name}`, 'event_label': `${gaGroup}:${kebabCaseTitle}:prev`, 'send_to': gaGroup });
    }  
  }

  const goToContact = () => {
    toggleModal();
    window.contactSource = {
      category: currentSection.name,
      label: `${gaGroup}:${kebabCaseTitle}:submission`
    }
    window.scrollTo(0, document.body.scrollHeight - 1200)

    // Track contact us click
    if (window.gtag) {
      window.gtag('event', 'contact-click', { 'event_category': `${currentSection.name}`, 'event_label': `${gaGroup}:${kebabCaseTitle}:contact-click`, 'send_to': gaGroup });
    }
  }

  const language = (window.localStorage.i18nextLng === 'ja') ? 'ja' : 'en';

  return (
    <StyledModal
      isOpen={open}
      onBackgroundClick={toggleModal}
      onEscapeKeydown={toggleModal}>
      <ModalBody id="modalBody" ref={modalRef}>
        <CloseButton src={CloseModalIcon} alt='Close' width='30px' height='30px' onClick={toggleModal} />
        <Heading id='heading'>
          <HeadingDescription>
            <Typography.Heading1>{current.title}</Typography.Heading1>
            {
                current.image && isMobile && (
                  <ImageContainer>
                    <img src={`${current.image}`} alt="" role="presentation" />
                  </ImageContainer>
                )
            }
            <LongDescription>
              <Typography.BodySmall>
                <HtmlText>{current.longDescription}</HtmlText>
              </Typography.BodySmall>
              <DescriptionSummary>
                <Subtitle>{current.descriptionSummary.heading}</Subtitle>
                <Typography.BodySmall>{current.descriptionSummary.content}</Typography.BodySmall>
              </DescriptionSummary>
            </LongDescription>
          </HeadingDescription>
          <HeadingImage>
            <LicensingCategories>
              {
                current.image && !isMobile && (
                  <ImageContainer>
                    <img src={`${current.image}`} alt="" role="presentation" />
                  </ImageContainer>
                )
              }
              <LicensingContent>
                <Subtitle>{t('PortfolioDetailModal.sections.licensingCategories.heading')}</Subtitle>
                <LicensingList>
                  {current.licensingCategories && current.licensingCategories.map(license => <License key={license}>{license}</License>)}
                </LicensingList>
              </LicensingContent>
            </LicensingCategories>
          </HeadingImage>
        </Heading>
        {
          isMobile ? (
            <PatentsCollapsed goToContact={()=>{goToContact()}} patents={current.patents} />
          ) :
            (
              <PatentsContainer>
                  {
                    (language === 'en') ? (
                      <PatentsSection>
                        <PatentHeading>
                          <PatentsTitle>
                            <Typography.BodyBold>{current.patents.title}</Typography.BodyBold>
                          </PatentsTitle>
                          <Subtitle>{current.patents.headline}</Subtitle>
                          <PatentsDescription> 
                            {
                              (current.patents.list.length > 0) && (
                                <Typography.BodySmall>
                                  <PatentDescription goToContact={()=>{goToContact()}} description={current.patents.description} />
                                </Typography.BodySmall>
                              )
                            }
                          </PatentsDescription>
                        </PatentHeading>
                        <PatentNumbers>
                          {
                            (current.patents.list.length > 0) && (
                              <USPatents>
                                <Subtitle>{current.patents.usPatents}</Subtitle>
                                <PatentsList patents={current.patents.list.length} >
                                  {current.patents.list.map(patent => <Patent key={patent}>{patent}</Patent>)}
                                </PatentsList>
                              </USPatents>
                            )
                          }
                          {
                            (current.patents.applications.length > 0) && (
                              <USPatentApplications>
                                <Subtitle>{current.patents.usPatentsApplications}</Subtitle>
                                <PatentsList patents={current.patents.applications.length}>
                                  {current.patents.applications.map(application => (
                                    <Patent key={application}>{application}</Patent>
                                  ))}
                                </PatentsList>
                              </USPatentApplications>
                            ) 
                          }
                        </PatentNumbers>
                        <PatentHeading>
                          <PatentsDescription>
                          {
                            (current.patents.jpList && current.patents.jpList.length > 0) && (
                              <Typography.BodySmall>
                              <PatentDescription goToContact={()=>{goToContact()}} description={current.patents.jpDescription} />
                            </Typography.BodySmall>
                            ) 
                          }
                          </PatentsDescription>
                        </PatentHeading>
                        <PatentNumbers>
                          {
                            (current.patents.jpList && current.patents.jpList.length > 0) && (
                              <USPatents>
                                <Subtitle>{current.patents.jpPatents}</Subtitle>
                                <PatentsList patents={current.patents.jpList.length} >
                                  {current.patents.jpList.map(patent => <Patent key={patent}>{patent}</Patent>)}
                                </PatentsList>
                              </USPatents>
                            )
                          }
                          {
                            (current.patents.jpApplications && current.patents.jpApplications.length > 0) && (
                              <USPatentApplications>
                                <Subtitle>{current.patents.jpPatentsApplications}</Subtitle>
                                <PatentsList patents={current.patents.jpApplications.length}>
                                  {current.patents.jpApplications.map(application => (
                                    <Patent key={application}>{application}</Patent>
                                  ))}
                                </PatentsList>
                              </USPatentApplications>
                            ) 
                          }
                        </PatentNumbers>
                        <PatentsReference>
                          {current.patents.disclaimers.map(disclaimer => <span key={disclaimer}>{disclaimer}</span>)}
                        </PatentsReference>
                      </PatentsSection>
                    ) : (
                      <PatentsSection> 
                        <PatentHeading>
                          <PatentsTitle>
                            <Typography.BodyBold>{current.patents.title}</Typography.BodyBold>
                          </PatentsTitle>
                          <Subtitle>{current.patents.headline}</Subtitle>
                          <PatentsDescription> 
                          {
                            (current.patents.jpList && current.patents.jpList.length > 0) && (
                              <Typography.BodySmall>
                              <PatentDescription goToContact={()=>{goToContact()}} description={current.patents.jpDescription} />
                            </Typography.BodySmall>
                            ) 
                          }
                          </PatentsDescription>
                        </PatentHeading>
                        <PatentNumbers>
                          {
                            (current.patents.jpList && current.patents.jpList.length > 0) && (
                              <USPatents>
                                <Subtitle>{current.patents.jpPatents}</Subtitle>
                                <PatentsList patents={current.patents.jpList.length} >
                                  {current.patents.jpList.map(patent => <Patent key={patent}>{patent}</Patent>)}
                                </PatentsList>
                              </USPatents>
                            )
                          }
                          {
                            (current.patents.jpApplications && current.patents.jpApplications.length > 0) && (
                              <USPatentApplications>
                                <Subtitle>{current.patents.jpPatentsApplications}</Subtitle>
                                <PatentsList patents={current.patents.jpApplications.length}>
                                  {current.patents.jpApplications.map(application => (
                                    <Patent key={application}>{application}</Patent>
                                  ))}
                                </PatentsList>
                              </USPatentApplications>
                            ) 
                          }
                        </PatentNumbers>
                        <PatentHeading>
                          <PatentsDescription> 
                            {
                              (current.patents.list.length > 0) && (
                                <Typography.BodySmall>
                                  <PatentDescription goToContact={()=>{goToContact()}} description={current.patents.description} />
                                </Typography.BodySmall>
                              )
                            }
                          </PatentsDescription>
                        </PatentHeading>
                        <PatentNumbers>
                          {
                            (current.patents.list.length > 0) && (
                              <USPatents>
                                <Subtitle>{current.patents.usPatents}</Subtitle>
                                <PatentsList patents={current.patents.list.length} >
                                  {current.patents.list.map(patent => <Patent key={patent}>{patent}</Patent>)}
                                </PatentsList>
                              </USPatents>
                            )
                          }
                          {
                            (current.patents.applications.length > 0) && (
                              <USPatentApplications>
                                <Subtitle>{current.patents.usPatentsApplications}</Subtitle>
                                <PatentsList patents={current.patents.applications.length}>
                                  {current.patents.applications.map(application => (
                                    <Patent key={application}>{application}</Patent>
                                  ))}
                                </PatentsList>
                              </USPatentApplications>
                            ) 
                          }
                        </PatentNumbers>
                        <PatentsReference>
                          {current.patents.disclaimers.map(disclaimer => <span key={disclaimer}>{disclaimer}</span>)}
                        </PatentsReference>
                    </PatentsSection>
                    )
                  }
              </PatentsContainer>
            )
        }
        {
          isMobile ? (
            <FaqCollapsed faqs={current.faqs} />
          ) :
          (
            <Faqs>
              <Typography.BodyBold>{t('PortfolioDetailModal.sections.faq.heading')}</Typography.BodyBold>
              <ListSection>
                <FaqsList>
                  {currentFaqs[0].map(faq => (
                      <Faq key={faq[0]}>
                        <Question>
                          <Subtitle>{faq[0]}</Subtitle>
                        </Question>
                        <Answer>
                          <Typography.BodySmall><HtmlText>{faq[1]}</HtmlText></Typography.BodySmall>
                        </Answer>
                      </Faq>
                    )
                  )}
                </FaqsList>
                <FaqsList>
                  {currentFaqs[1].map(faq => (
                    <Faq key={faq[0]}>
                      <Question>
                        <Subtitle>{faq[0]}</Subtitle>
                      </Question>
                      <Answer>
                        <Typography.BodySmall><HtmlText>{faq[1]}</HtmlText></Typography.BodySmall>
                      </Answer>
                    </Faq>
                  )
                )}
                </FaqsList>
              </ListSection>
            </Faqs>
          )
        }
        <Footer>
          <FooterHeading>
            <Typography.BodyBold>{t('PortfolioDetailModal.sections.selector.heading')}</Typography.BodyBold>
          </FooterHeading>
          <SelectorSection>
            <PreviousPortfolio>
              <PortfolioTitle>{prev.title}</PortfolioTitle>
              <PortfolioDescription>
                {reduceDescription(prev.longDescription)}
              </PortfolioDescription>
              <NextPortolioButton right={false} onClick={e => showPrev(e)}>
                <Arrow>{'<'}</Arrow>&nbsp;{t('PortfolioDetailsControls.prev')}
              </NextPortolioButton>
            </PreviousPortfolio>
            <NextPortfolio>
              <PortfolioTitle>{next.title}</PortfolioTitle>
              <PortfolioDescription>
                {reduceDescription(next.longDescription)}
              </PortfolioDescription>
              <NextPortolioButton href='#heading' right={true} onClick={e => showNext(e)}>
                {t('PortfolioDetailsControls.next')}&nbsp;<Arrow>{'>'}</Arrow>
              </NextPortolioButton>
            </NextPortfolio>
          </SelectorSection>
        </Footer>
        <CTAWrapper>
          <ToyotaButton onClick={goToContact}>{t('SiteNavData.contact.title')}</ToyotaButton>
        </CTAWrapper>
      </ModalBody>
    </StyledModal>
  )
}